import React, { useEffect } from "react";
import { useRoutes, RouteObject, useLocation, Navigate, useNavigate, matchRoutes } from "react-router-dom";
import { useSelector } from "react-redux";
import { Route, routes } from "./routes";
import { AnimatePresence, motion, Variants } from "framer-motion";
import { App as CapacitorApp } from '@capacitor/app';
import { initAnalytics, analyticsLog } from "../lib/analytics"; // Import your new utility
import { useDocumentTitle } from "../hooks/useDocumentTitle";
import { selectNeedsTimezone } from "../redux-stores/slices/authSlice";

export const pageVariants: Variants = {
    initial: { opacity: 0, x: 100 },
    animate: {
        opacity: 1,
        x: 0,
        transition: { duration: 0.1, ease: "easeInOut" }
    },
    exit: {
        opacity: 0,
        x: -100,
        transition: { duration: 0.1, ease: "easeInOut" }
    }
};

const mapRoutes = (routes: Route[]): RouteObject[] => {
    return routes.map(route => {
        const { element, children, props, redirect, ...rest } = route;
        return {
            ...rest,
            element: redirect ? <Navigate to={redirect} /> : (
                <motion.div
                    key={route.path}
                    variants={pageVariants}
                    initial="initial"
                    animate="animate"
                    exit="exit"
                    style={{ width: "100%" }}
                >
                    {React.createElement(element, props || {})}
                </motion.div>
            ),
            children: children ? mapRoutes(children) : undefined,
        };
    });
};

const AppRoutes = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const needsTimezone = useSelector(selectNeedsTimezone);
    useDocumentTitle();

    // 1. Initialize Analytics & Track Page Views
    useEffect(() => {
        window.scrollTo(0, 0);
    }, [location.pathname]);

    // 2. Capacitor Back Button Handler
    useEffect(() => {
        const backHandler = CapacitorApp.addListener('backButton', () => {
            if (needsTimezone) return;

            const path = window.location.pathname;
            if (path === '/' || path === '/home' || window.history.length <= 1) {
                CapacitorApp.exitApp();
            } else {
                navigate(-1);
            }
        });
        return () => { backHandler.then(h => h.remove()); };
    }, [navigate, needsTimezone]);

    const routeObjects = mapRoutes(routes);
    const element = useRoutes(routeObjects, location);

    return (
        <AnimatePresence mode="wait">
            {/* Cloned element with key is required for Framer Motion exit transitions to work with useRoutes */}
            {useRoutes(routeObjects)}
        </AnimatePresence>
    );
};

export default AppRoutes;